// Hideo - tags of Hooks, Sources and Saved videos: per-project tag lists, the tags of the filter bar and the grouping of
// items by tag. Tags are per project: data.projectTags = { <project id>: { hook: [...], src: [...], saved: [...] } }
// (normalizeData builds them from the old profile lists and always adds the tags used by items). Tag names are compared without case (src/data/tags.ts).
// The tag picker of the Vue dialogs is src/components/fields/TagPicker.vue, the "Manage tags" lists src/components/TagList.vue
// (dialog: src/components/dialogs/TagManagerDialog.vue); the tag labels of the cards are tagBadgesHtml (ported from the
// legacy ui/tags.js, verbatim copy in tests/fixtures/legacy/tags.js; with the bar's projects: tagBadges in src/stores/tags.ts).
import { TAG_NAME_MAX, hasTag, sameTag, uniqTags } from '../data/tags'
import type { ProfileData, Project, TagGroup, TagLook } from '../data/types'
import { esc } from '../utils/html'
import { t } from '../i18n'
import { lookChipStyle, lookIconHtml, moveTagLook, tagLook } from './looks'
import { ALL_PROJECTS } from './projects'
import { moveItem } from './dragSort'

/** Name and icon of a tag group (Settings › Tags, the icon/colour dialog) */
export const tagGroupName = (k: TagGroup): string => (k === 'hook' ? t('Hooks tags') : k === 'src' ? t('Sources tags') : t('Saved videos tags'))
export const TAG_GROUP_ICON: Record<TagGroup, string> = { hook: 'fa-anchor', src: 'fa-link', saved: 'fa-bookmark' }

/** Anything with tags (a hook, a source or a saved video) */
interface Tagged {
  tags: string[]
  projectId?: string
}

/** Tag list of a project, created in the data when missing (the returned array is the stored one: changes are kept) */
export function tagList(d: ProfileData, k: TagGroup, pid: string): string[] {
  if (!d.projectTags || typeof d.projectTags !== 'object') d.projectTags = {}
  const e = d.projectTags[pid] || (d.projectTags[pid] = { hook: [], src: [], saved: [] })
  if (!Array.isArray(e[k])) e[k] = []
  return e[k]
}

/** Projects whose tags appear in the bar: the active one, or all of them with "All projects" (active = 'all') */
export const barProjects = (d: ProfileData, active: string): Project[] => (active === ALL_PROJECTS ? d.projects : d.projects.filter(p => p.id === active))

/** Tag labels of a card (Hooks and Sources pages), with the colour and icon chosen for each tag, otherwise in the main
 *  colour; HTML (names escaped). Without pid the look comes from the first project in `order` (the bar's) that has one */
export function tagBadgesHtml(d: ProfileData, tags: string[], k: TagGroup | '' | null | undefined, pid?: string, order?: readonly string[]): string {
  return tags.map(tag => {
    const l: TagLook = k ? tagLook(d, k, tag, pid, order) : {}
    return `<span class="inline-flex items-center gap-1 text-[10px] ${l.color ? '' : 'bg-red-950/60 text-brand border-red-900/40'} backdrop-blur-md px-2.5 py-0.5 rounded-full font-bold border" style="${lookChipStyle(l)}">${lookIconHtml(l, 'text-[9px]', 'w-3 h-3')}${esc(tag)}</span>`
  }).join('')
}

/** Tags of the bar: those of the bar's projects, without case-insensitive duplicates */
export const barTagsOf = (d: ProfileData, k: TagGroup, active: string): string[] =>
  uniqTags(barProjects(d, active).flatMap(p => tagList(d, k, p.id)))

/** Projects that have a tag (the coloured dots of a bar chip with "All projects") */
export const projectsWithTag = (d: ProfileData, k: TagGroup, tag: string): Project[] => d.projects.filter(p => hasTag(tagList(d, k, p.id), tag))

/** Tag filter value for "No tag" (a tag name cannot start with a NUL character) */
export const TAG_NONE = '\u0000none'
/** Items with a tag filter applied: '' = all, TAG_NONE = without tags, otherwise a tag name (compared without case) */
export function ofTag<T extends Tagged>(list: T[], tag: string): T[] {
  if (!tag) return list
  if (tag === TAG_NONE) return list.filter(x => !x.tags.length)
  return list.filter(x => hasTag(x.tags, tag))
}

/** How many items have a tag */
export const countWithTag = (items: readonly Tagged[], tag: string): number => items.filter(x => hasTag(x.tags, tag)).length

export interface TagGroupOf<T> {
  /** '' for the "No tag" group */
  tag: string
  items: T[]
  none: boolean
}

/** One group per tag that has items (or only the filtered tag, even when empty) + "No tag" without a filter.
 *  An item with several tags appears in several groups. */
export function groupByTag<T extends Tagged>(items: readonly T[], tags: readonly string[], filter: string): TagGroupOf<T>[] {
  const groups: TagGroupOf<T>[] = (filter ? [filter] : tags)
    .map(tag => ({ tag, items: items.filter(x => hasTag(x.tags, tag)), none: false }))
    .filter(g => g.items.length || filter)
  const untagged = items.filter(x => !x.tags.length)
  if (!filter && untagged.length) groups.push({ tag: '', items: untagged, none: true })
  return groups
}

/** Adds a tag to a project (unless it exists already, case-insensitive). Returns the tag as stored and whether it is new. */
export function addTag(d: ProfileData, k: TagGroup, pid: string, name: string): { tag: string; created: boolean } | null {
  const clean = name.trim().slice(0, TAG_NAME_MAX)
  if (!clean) return null
  const L = tagList(d, k, pid)
  const found = L.find(x => sameTag(x, clean))
  if (found) return { tag: found, created: false }
  L.push(clean)
  return { tag: clean, created: true }
}

/** Tags offered by a dialog's tag picker: the project's tags, plus the chosen ones it lacks (they are kept on the item) */
export const pickerTags = (d: ProfileData, k: TagGroup, pid: string, sel: string[]): string[] => uniqTags([...tagList(d, k, pid), ...sel])

/** The chosen tags after a tap on one (case-insensitive: a chosen tag is removed, another one is added) */
export const toggleTag = (sel: string[], tag: string): string[] => (hasTag(sel, tag) ? sel.filter(x => !sameTag(x, tag)) : [...sel, tag])

// ===== Manage tags: rename (or merge into an existing tag) and delete, within a project =====
// The lists are src/components/TagList.vue ("Manage tags" dialog and Settings › Tags)

/** Where the items of each group live in the data */
const TAG_ITEMS = { hook: 'vault', src: 'sources', saved: 'savedVideos' } as const
/** Items of a group in a project */
export const projectItems = (d: ProfileData, k: TagGroup, pid: string): Tagged[] =>
  (d[TAG_ITEMS[k]] as Tagged[]).filter(x => x.projectId === pid)

/** What renaming `old` to `name` does in a project: nothing ('' or the same name), a plain rename, or a merge into
 *  another existing tag (`other`, found without case: the user is asked first) */
export function renamePlan(d: ProfileData, k: TagGroup, pid: string, old: string, name: string): { name: string; other: string } | null {
  const clean = name.trim().slice(0, TAG_NAME_MAX)
  if (!clean || clean === old) return null
  return { name: clean, other: tagList(d, k, pid).find(x => x !== old && sameTag(x, clean)) || '' }
}

/** Renames `old` to `name` in a project's list and items, or merges it into `other` (the items get it, without
 *  duplicates); the tag's look follows. Returns the tag the items now have. */
export function renameTag(d: ProfileData, k: TagGroup, pid: string, old: string, name: string, other: string): string {
  const L = tagList(d, k, pid), target = other || name
  projectItems(d, k, pid).forEach(x => { x.tags = uniqTags(x.tags.map(y => (sameTag(y, old) ? target : y))) })
  d.projectTags![pid][k] = other ? L.filter(x => x !== old) : L.map(x => (x === old ? name : x))
  moveTagLook(d, k, pid, old, target)
  return target
}

/** Moves a tag of a project to another place of its list (drag to reorder in TagList.vue): the list's order is the order of
 *  the tag bar, the tag picker and the lists of tags. False when nothing changed. */
export function moveTag(d: ProfileData, k: TagGroup, pid: string, from: number, to: number): boolean {
  return moveItem(tagList(d, k, pid), from, to)
}

/** Deletes a tag from a project and from its items (the items stay); its look goes too */
export function removeTag(d: ProfileData, k: TagGroup, pid: string, tag: string): void {
  projectItems(d, k, pid).forEach(x => { x.tags = x.tags.filter(y => !sameTag(y, tag)) })
  d.projectTags![pid][k] = tagList(d, k, pid).filter(x => !sameTag(x, tag))
  moveTagLook(d, k, pid, tag, '')
}
